import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { ChatMessage } from '../components/ChatMessage';
import { ChatInput } from '../components/ChatInput';
import { TypingIndicator } from '../components/TypingIndicator';
import { NyxtLogo } from '../components/NyxtLogo';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logger utility to replace console statements
const logger = {
  error: (message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }
};

// Loading component
const LoadingScreen = () => (
  <div 
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: '#F9F8F6' }}
  >
    <NyxtLogo size={64} className="animate-pulse" />
  </div>
);

// Empty state component
const EmptyState = ({ greeting }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
    <NyxtLogo size={80} className="mb-6 opacity-80" />
    <h1 
      className="text-4xl sm:text-5xl font-medium tracking-tight mb-4"
      style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#1A1D1A' }}
    >
      {greeting}.
    </h1>
    <p 
      className="text-lg max-w-md"
      style={{ color: '#596059' }}
    >
      How can I assist you today?
    </p>
  </div>
);

// Messages list component
const MessagesList = ({ messages, currentUser, isTyping, messagesEndRef }) => (
  <>
    {messages.map((message, index) => (
      <ChatMessage 
        key={message.message_id || index} 
        message={message} 
        user={currentUser}
      />
    ))}
    {isTyping && (
      <div className="flex gap-4">
        <div className="w-8 h-8 flex items-center justify-center">
          <NyxtLogo size={28} />
        </div>
        <TypingIndicator />
      </div>
    )}
    <div ref={messagesEndRef} />
  </>
);

// Mobile header component
const MobileHeader = ({ onSidebarOpen }) => (
  <div className="md:hidden flex items-center gap-3 p-4 border-b" style={{ borderColor: '#DCD7CE' }}>
    <Button
      variant="ghost"
      size="icon"
      onClick={onSidebarOpen}
      data-testid="sidebar-toggle"
    >
      <Menu className="w-5 h-5" />
    </Button>
    <NyxtLogo size={28} />
    <span 
      className="font-medium"
      style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#1A1D1A' }}
    >
      NYXT AI
    </span>
  </div>
);

// Input area component
const InputArea = ({ onSend, isLoading, webSearchEnabled, onWebSearchToggle }) => (
  <div className="absolute bottom-0 left-0 right-0 input-gradient pt-10 pb-6 px-4 sm:px-6">
    <div className="max-w-3xl mx-auto">
      <ChatInput
        onSend={onSend}
        disabled={isLoading}
        webSearchEnabled={webSearchEnabled}
        onWebSearchToggle={onWebSearchToggle}
        isSearching={isLoading && webSearchEnabled}
      />
      <p 
        className="text-center text-xs mt-3"
        style={{ color: '#8A908A' }}
      >
        NYXT AI can make mistakes. Consider checking important information.
      </p>
    </div>
  </div>
);

export const Dashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const messagesEndRef = useRef(null);

  // Memoized current user
  const currentUser = useMemo(() => user || location.state?.user, [user, location.state?.user]);

  // Memoized greeting
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Check auth
  useEffect(() => {
    if (!location.state?.user && !authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate, location.state?.user]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      logger.error('Failed to fetch conversations:', error);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchConversations();
    }
  }, [currentUser, fetchConversations]);

  // Create new conversation
  const handleNewChat = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/conversations`, 
        { title: 'New Chat' },
        { withCredentials: true }
      );
      setConversations(prev => [response.data, ...prev]);
      setCurrentConversation(response.data);
      setMessages([]);
      setSidebarOpen(false);
    } catch (error) {
      logger.error('Failed to create conversation:', error);
    }
  }, []);

  // Select conversation
  const handleSelectConversation = useCallback(async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        withCredentials: true
      });
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
      setSidebarOpen(false);
    } catch (error) {
      logger.error('Failed to fetch conversation:', error);
    }
  }, []);

  // Delete conversation
  const handleDeleteConversation = useCallback(async (conversationId) => {
    try {
      await axios.delete(`${API}/conversations/${conversationId}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      setCurrentConversation(prev => {
        if (prev?.conversation_id === conversationId) {
          setMessages([]);
          return null;
        }
        return prev;
      });
    } catch (error) {
      logger.error('Failed to delete conversation:', error);
    }
  }, []);

  // Send message
  const handleSendMessage = useCallback(async (messageText) => {
    if (!messageText.trim()) return;
    
    let targetConversation = currentConversation;
    
    // Create conversation if none selected
    if (!targetConversation) {
      try {
        const response = await axios.post(`${API}/conversations`, 
          { title: 'New Chat' },
          { withCredentials: true }
        );
        targetConversation = response.data;
        setCurrentConversation(response.data);
        setConversations(prev => [response.data, ...prev]);
      } catch (error) {
        logger.error('Failed to create conversation:', error);
        return;
      }
    }

    const userMessage = {
      message_id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/send`, {
        conversation_id: targetConversation.conversation_id,
        message: messageText,
        web_search_enabled: webSearchEnabled
      }, { withCredentials: true });

      setMessages(prev => {
        const filtered = prev.filter(m => m.message_id !== userMessage.message_id);
        return [...filtered, response.data.user_message, response.data.assistant_message];
      });

      // Update conversation title for first message
      setConversations(prev => prev.map(c => 
        c.conversation_id === targetConversation.conversation_id && c.title === 'New Chat'
          ? { ...c, title: messageText.slice(0, 50) + (messageText.length > 50 ? '...' : '') }
          : c
      ));
    } catch (error) {
      logger.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.message_id !== userMessage.message_id));
    } finally {
      setIsTyping(false);
      setIsLoading(false);
    }
  }, [currentConversation, webSearchEnabled]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  // Toggle web search
  const handleWebSearchToggle = useCallback(() => {
    setWebSearchEnabled(prev => !prev);
  }, []);

  // Toggle sidebar
  const handleSidebarOpen = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  if (authLoading && !location.state?.user) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#F9F8F6' }}>
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar
          conversations={conversations}
          currentConversationId={currentConversation?.conversation_id}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          user={currentUser}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <Sidebar
            conversations={conversations}
            currentConversationId={currentConversation?.conversation_id}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleNewChat}
            onDeleteConversation={handleDeleteConversation}
            user={currentUser}
            onLogout={handleLogout}
          />
        </SheetContent>
      </Sheet>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
        <MobileHeader onSidebarOpen={handleSidebarOpen} />

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto smooth-scroll">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-32 space-y-8">
            {messages.length === 0 ? (
              <EmptyState greeting={greeting} />
            ) : (
              <MessagesList 
                messages={messages}
                currentUser={currentUser}
                isTyping={isTyping}
                messagesEndRef={messagesEndRef}
              />
            )}
            {messages.length > 0 && <div ref={messagesEndRef} />}
          </div>
        </div>

        <InputArea 
          onSend={handleSendMessage}
          isLoading={isLoading}
          webSearchEnabled={webSearchEnabled}
          onWebSearchToggle={handleWebSearchToggle}
        />
      </div>
    </div>
  );
};

export default Dashboard;
